import "./index.scss";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, withRouter } from "react-router-dom";
import DatePicker from "react-datepicker";
import InputSearch from "../../ui/InputSearch";
import InspectionDetailsModal from "../../ui/InspectionDetailsModal";
import ConfirmModal from "../../ui/ConfirmModal";
import UpdateInspectionModal from "../../ui/UpdateInspectionModal";
import UpdateAddressModal from "../../ui/UpdateAddressModal";
import v1, { v2 } from "../../services/axios";
import moment from "moment";

// Helper Button component for DRYness
const ActionButton = ({ className, disabled, onClick, children }) => (
  <button
    className={className}
    disabled={disabled}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

const InspectionDetails = (props) => {
  const {
    report,
    role,
    mdPeople,
    userCanEdit,
    updatingReport,
    reportID,
    getOneReport,
    updateReport,
    submitForReview,
    assignAnalyst,
  } = props;

  const [date, setDate] = useState(new Date());
  const [inspectionPerson, setInspectionPerson] = useState(null);
  const [inspectionCompany, setInspectionCompany] = useState(null);
  const [agent, setAgent] = useState(null);
  const [allInspectors, setAllInspectors] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [allBrokersData, setAllBrokersData] = useState(null);
  const [allInspectorsData, setAllInspectorsData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [majordomoPeopleRefId, setMajordomoPeopleRefId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showAutomationConfirm, setShowAutomationConfirm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalLabel, setModalLabel] = useState("");
  const [message, setMessage] = useState("");
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);

  // Track if data has been loaded to prevent duplicate API calls
  const dataLoadedRef = useRef(false);

  // API Methods using v1 service
  const getInspectors = useCallback(async () => {
    try {
      const response = await v1.get(`/domoreport/uploader/allInspectors`);
      return response.data;
    } catch (error) {
      console.error("Error fetching inspectors:", error);
      throw error;
    }
  }, []);

  const getBrokers = useCallback(async () => {
    try {
      const response = await v1.get(`/domoreport/uploader/allBrokers`);
      return response.data;
    } catch (error) {
      console.error("Error fetching brokers:", error);
      throw error;
    }
  }, []);

  const loadBrokersAndInspectors = useCallback(async () => {
    // Prevent duplicate calls
    if (dataLoadedRef.current) {
      return;
    }

    try {
      dataLoadedRef.current = true;
      const [brokersData, inspectorsData] = await Promise.all([
        getBrokers(),
        getInspectors(),
      ]);

      setAllBrokersData(brokersData);
      setAllInspectorsData(inspectorsData);
    } catch (error) {
      console.error("Error loading brokers and inspectors:", error);
      dataLoadedRef.current = false; // Reset on error so it can be retried
    }
  }, [getBrokers, getInspectors]);

  // Initialize component on mount and when report changes
  useEffect(() => {
    if (Object.keys(report).length > 0) {
      let newDate = new Date();
      let yesterday = newDate.getDate() - 1;
      newDate.setDate(yesterday);
      newDate.setHours(12, 0, 0);

      setDate(
        report.inspection_datetime
          ? new Date(report.inspection_datetime)
          : newDate
      );
      setWeather(report.inspection_weather_comment);
      setMajordomoPeopleRefId(report.majordomo_people_ref_id);

      // Set inspection company if available
      if (report.inspector_ref_id) {
        setInspectionCompany((prev) =>
          prev
            ? prev
            : {
                id: report.inspector_ref_id,
                name: report.inspector_person_name,
                company: report.inspection_company,
              }
        );
      }

      // Set agent if available
      if (report.broker_ref_id) {
        setAgent((prev) =>
          prev
            ? prev
            : {
                id: report.broker_ref_id,
                name: report.broker_person_name,
                company: report.brokerage,
              }
        );
      }
    }
  }, [report]);

  // Load brokers and inspectors data once when component mounts
  useEffect(() => {
    loadBrokersAndInspectors();
  }, [loadBrokersAndInspectors]);

  // API Methods using v1 service
  const publishReport = useCallback(async () => {
    try {
      const response = await v1.put(
        `/domoreport/uploader/publishDomoreport/${reportID}`
      );
      if (response.data && response.data.success) {
        getOneReport();
      }
      return response.data;
    } catch (error) {
      console.error("Error publishing report:", error);
      throw error;
    }
  }, [reportID, getOneReport]);

  const placeOnHold = useCallback(async () => {
    try {
      const response = await v1.patch(`/domoreport/${reportID}/on-hold`);
      getOneReport();
      return response.data;
    } catch (error) {
      console.error("Error placing report on hold:", error);
      throw error;
    }
  }, [reportID, getOneReport]);

  const toggleModal = useCallback(
    (e, label) => {
      e.preventDefault();
      setShowModal(!showModal);
      setModalLabel(label);
      // Clear inspectionPerson when opening modal for "Add New" (when coming from InputSearch)
      if (!showModal && label) {
        setInspectionPerson(null);
      }
    },
    [showModal]
  );

  const toggleConfirmModal = useCallback(() => {
    setShowConfirmModal(!showConfirmModal);
  }, [showConfirmModal]);

  const toggleAutomationConfirm = useCallback(() => {
    setShowAutomationConfirm(!showAutomationConfirm);
  }, [showAutomationConfirm]);

  // Handle Inspector/Agent search
  const handleSearch = useCallback(
    (key, value) => {
      let results = [];

      if (value == null) {
        value = "";
      }

      if (allInspectorsData && key === "inspection_company") {
        allInspectorsData.inspectors &&
          allInspectorsData.inspectors.map((inspector) => {
            if (
              (inspector.inspection_company &&
                inspector.inspection_company
                  .toUpperCase()
                  .includes(value.toUpperCase())) ||
              (inspector.inspector_person_name &&
                inspector.inspector_person_name
                  .toUpperCase()
                  .includes(value.toUpperCase()))
            ) {
              let result = {
                id: inspector.inspector_ref_id,
                name: inspector.inspector_person_name.trim(),
                company: inspector.inspection_company.trim(),
                email: inspector.inspection_email,
                phone: inspector.inspection_phone,
              };
              results.push(result);
            }
            return null;
          });

        // Only update the search results, not the selected value
        setAllInspectors(results);
      } else if (allBrokersData && key === "agent") {
        allBrokersData.brokers &&
          allBrokersData.brokers.map((broker) => {
            if (
              (broker.brokerage &&
                broker.brokerage.toUpperCase().includes(value.toUpperCase())) ||
              (broker.broker_person_name &&
                broker.broker_person_name
                  .toUpperCase()
                  .includes(value.toUpperCase()))
            ) {
              let result = {
                id: broker.broker_ref_id,
                name: broker.broker_person_name || "",
                company: broker.brokerage || "",
                email: broker.brokerage_email,
                phone: broker.brokerage_phone,
              };
              results.push(result);
            }
            return null;
          });

        // Only update the search results, not the selected value
        setAllAgents(results);
      }
    },
    [allInspectorsData, allBrokersData]
  );

  const updateReportHandler = useCallback(
    (e) => {
      e.preventDefault();

      let data = {
        inspection_datetime: date.toISOString().slice(0, 19).replace("T", " "),
        inspector_ref_id: inspectionCompany ? inspectionCompany.id : null,
        broker_ref_id: agent ? agent.id : null,
        inspection_weather_comment: weather,
      };

      if (report.majordomo_notes === null || report.majordomo_notes === "") {
        data.majordomo_notes =
          "-Any reference to mold and/or bacterial growths should be reviewed by a specialist.\n\n-All structural and foundation issues should be reviewed by a structural specialist. \n\n-When needed, anti-tip devices for kitchen stoves and anti-siphon devices for spigots can be purchased at your local home store for $5-$10 and are easily installed.";
      }

      updateReport(data);
    },
    [
      date,
      inspectionCompany,
      agent,
      weather,
      report.majordomo_notes,
      updateReport,
    ]
  );

  const handlePublishClick = useCallback((e) => {
    e.preventDefault();
    setShowPublishConfirmModal(true);
  }, []);

  const handlePublishConfirm = useCallback(() => {
    setShowPublishConfirmModal(false);
    let queued = JSON.parse(localStorage.getItem("queued-projects"));
    if (queued) {
      delete queued[report.unique_id];
      localStorage.setItem("queued-projects", JSON.stringify(queued));
    }
    publishReport();
  }, [report.unique_id, publishReport]);

  const handlePublishCancel = useCallback(() => {
    setShowPublishConfirmModal(false);
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (role === "PUBLISHER") {
        if (report.domoreport_published) {
          updateReport({
            domoreport_published: 0,
            status: "READY",
            report_published_at: null,
          });
        } else if (!report.domoreport_published) {
          // Show confirmation modal instead of publishing immediately
          handlePublishClick(e);
          return;
        }
      } else if (role === "ANALYST") {
        if (majordomoPeopleRefId) {
          toggleConfirmModal();
        } else {
          if (assignAnalyst) {
            assignAnalyst();
          }
        }
      }
    },
    [
      role,
      report.domoreport_published,
      majordomoPeopleRefId,
      updateReport,
      handlePublishClick,
      toggleConfirmModal,
      assignAnalyst,
    ]
  );

  const handleEditPerson = useCallback(
    (e, type) => {
      e.preventDefault();

      let person = null;
      if (type === "agent") {
        person = {
          id: agent.id || report.broker_ref_id,
          name: agent.name || report.broker_person_name,
          company: agent.company || report.brokerage,
          email: agent.email || report.brokerage_email,
          phone: agent.phone || report.brokerage_phone,
        };
      } else if (type === "inspection_company") {
        person = {
          id: inspectionCompany.id || report.inspector_ref_id,
          name: inspectionCompany.name || report.inspector_person_name,
          company: inspectionCompany.company || report.inspection_company,
          email: inspectionCompany.email || report.inspection_email,
          phone: inspectionCompany.phone || report.inspection_phone,
        };
      }

      setModalLabel(type);
      setInspectionPerson(person);
      setShowModal(true);
    },
    [agent, inspectionCompany, report]
  );

  const toggleInspectionModal = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      setShowInspectionModal(!showInspectionModal);
    },
    [showInspectionModal]
  );

  const toggleAddressModal = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      setShowAddressModal(!showAddressModal);
    },
    [showAddressModal]
  );

  const startAutomationService = useCallback(
    async (id) => {
      try {
        const { status } = await v2.post(`/domoreports/${id}/automation`);
        if (status === 201) {
          setShowAutomationConfirm(false);
          getOneReport();
        }
      } catch (error) {
        console.error(error);
      }
    },
    [getOneReport]
  );

  const handleAutomationRequest = useCallback(() => {
    if (report.submitted_for_identification_at) {
      toggleAutomationConfirm();
    } else {
      startAutomationService(report.unique_id);
    }
  }, [
    report.submitted_for_identification_at,
    report.unique_id,
    toggleAutomationConfirm,
    startAutomationService,
  ]);

  const renderActionButtons = useCallback(() => {
    let action = "Publish Report";
    let status = "primary";
    if (role === "PUBLISHER") {
      if (report.domoreport_published) {
        action = "Unpublish Report";
        status = "danger";
      }
    } else if (role === "ANALYST") {
      status = report.status;
      if (!majordomoPeopleRefId) {
        action = "Claim Domoreport";
      } else if (majordomoPeopleRefId) {
        action = "Submit For Review";
      } else if (report.status === "IN_REVIEW") {
        action = "In Review";
      } else if (report.domoreport_published) {
        action = "Published";
      }
    }
    return (
      <div className="row">
        {role === "PUBLISHER" && (
          <div className="col-12 col-lg-4">
            <ActionButton
              className={`btn btn-primary ${!userCanEdit ? "d-none" : ""}`}
              onClick={
                report.status === "ON_HOLD"
                  ? () => updateReport({ status: "READY" })
                  : placeOnHold
              }
              // disabled={!userCanEdit || report.status === "ON_HOLD"}
            >
              {report.status === "ON_HOLD" ? "Take off hold" : "Place on hold"}
            </ActionButton>
          </div>
        )}

        {role === "ANALYST" ? (
          <div className="col-12 col-lg-4">
            <ActionButton
              className={`btn btn-${status} ${
                updatingReport ? "disabled" : ""
              }`}
              disabled={updatingReport}
              onClick={handleSubmit}
            >
              {action}
            </ActionButton>
          </div>
        ) : (
          <div className="col-12 col-lg-4">
            <ActionButton
              className={`btn btn-${status} ${
                updatingReport ? "disabled" : ""
              } ${!userCanEdit ? "d-none" : ""}`}
              disabled={!userCanEdit}
              onClick={handleSubmit}
            >
              {action}
            </ActionButton>
          </div>
        )}

        {role === "PUBLISHER" && (
          <div className="col-12 col-lg-4">
            <ActionButton
              className={`btn btn-primary ${
                updatingReport || report.status === "ON_HOLD" ? "disabled" : ""
              } ${!userCanEdit ? "d-none" : ""}`}
              onClick={() => handleAutomationRequest()}
              disabled={
                !userCanEdit ||
                report.status === "IDENTIFYING" ||
                report.status === "CLASSIFYING"
              }
            >
              Start Automation
            </ActionButton>
          </div>
        )}
      </div>
    );
  }, [
    role,
    report,
    majordomoPeopleRefId,
    updatingReport,
    userCanEdit,
    updateReport,
    placeOnHold,
    handleSubmit,
    handleAutomationRequest,
  ]);

  return (
    <div>
      <div style={{ position: "absolute", left: "-5000px" }} aria-hidden="true">
        <input
          type="text"
          name="email"
          id="email"
          tabIndex="-1"
          value=""
          onChange={() => {
            return null;
          }}
        />
      </div>

      {showModal && (
        <InspectionDetailsModal
          getBrokers={getBrokers}
          getInspectors={getInspectors}
          handleChange={(key, value) => {
            switch (key) {
              case "inspection_company":
                setInspectionCompany(value);
                break;
              case "agent":
                setAgent(value);
                break;
              default:
                break;
            }
          }}
          inspectionPerson={inspectionPerson}
          label={modalLabel}
          toggleModal={toggleModal}
        />
      )}

      <ConfirmModal
        action="Submit"
        close={toggleConfirmModal}
        handleSubmit={submitForReview}
        subtext="You will not be able to make changes after submitting this report for review"
        title="Submit For Review?"
        variant="warning"
        show={showConfirmModal}
      />

      <ConfirmModal
        action="Re-run Automation"
        close={toggleAutomationConfirm}
        handleSubmit={() => startAutomationService(report.unique_id)}
        subtext={`Automation service ran on ${moment(
          report.submitted_for_identification_at
        ).format(
          "MMMM Do YYYY"
        )}. Are you sure you want to run it again. Projects will be duplicated`}
        title="Re-run Automation Service?"
        variant="danger"
        show={showAutomationConfirm}
      />

      {showInspectionModal && (
        <UpdateInspectionModal
          reportId={report.domoreport_log_id}
          close={toggleInspectionModal}
        />
      )}

      {showAddressModal && (
        <UpdateAddressModal
          reportId={report.domoreport_log_id}
          close={toggleAddressModal}
        />
      )}

      {message && (
        <div className="md-diy-sidebar-message">
          <p>{message}</p>
          <p onClick={() => setMessage(null)}>x</p>
        </div>
      )}

      <div className={`md-diy-sidebar-content ${showModal ? "blur" : ""}`}>
        <div className="md-diy-sidebar-info">
          <h3 className="md-diy-sidebar-header mb-0">Inspection Details</h3>

          <div className="admin-links-wrapper">
            <div>
              <Link
                className="md-legacy-link"
                to={`/report/${report.domoreport_log_id}`}
              >
                <i className="fa fa-arrow-circle-left mr-1" />
                Legacy View
              </Link>
            </div>

            {role === "PUBLISHER" && (
              <div>
                <a href="/" onClick={toggleInspectionModal}>
                  <i className="fa fa-file-pdf-o mr-1" /> Update Inspection PDF
                </a>
              </div>
            )}

            {role === "PUBLISHER" && (
              <div>
                <a href="/" onClick={toggleAddressModal}>
                  <i className="fa fa-home mr-1" />
                  Update Property Address
                </a>
              </div>
            )}

            {/* {role === "PUBLISHER" && report.status !== "CANCELLED" && (
              <div>
                <a href="/" onClick={(e) => cancelReport(e, report.unique_id)}>
                  <i className="fa fa-times-circle mr-1" />
                  Cancel Report
                </a>
              </div>
            )} */}
          </div>

          <p className="m-0">
            <b>Identification started</b>:{" "}
            {report.submitted_for_identification_at ? (
              <span>
                {moment(report.submitted_for_identification_at).format(
                  "MMMM Do YYYY"
                )}{" "}
                at{" "}
                {moment(report.submitted_for_identification_at).format(
                  "h:mm:ss a"
                )}
              </span>
            ) : (
              "N/A"
            )}
          </p>

          <p className="m-0">
            <b>Classification started</b>:{" "}
            {report.submitted_for_classification_at ? (
              <span>
                {moment(report.submitted_for_classification_at).format(
                  "MMMM Do YYYY"
                )}{" "}
                at{" "}
                {moment(report.submitted_for_classification_at).format(
                  "h:mm:ss a"
                )}
              </span>
            ) : (
              "N/A"
            )}
          </p>

          <p>
            <b>Status</b>: <span>{report.status}</span>
          </p>

          <hr />
        </div>

        <div className="md-diy-sidebar-form">
          {report.domoscore_notes && <label>Customer Notes</label>}
          <p>{report.domoscore_notes}</p>

          {report.domoscore_notes && <hr />}

          <label>Date of Inspection</label>
          <div className="md-diy-input-wrapper">
            <img
              src="/assets/icons/Date.svg"
              className="md-diy-input-icon"
              alt="Date"
            />
            <DatePicker
              disabled={!userCanEdit}
              selected={date || new Date(report.inspection_datetime)}
              onSelect={(value) => setDate(value)}
            />
          </div>

          <label>
            Inspection Company{" "}
            {!!inspectionCompany && (
              <a
                href="/"
                onClick={(e) => handleEditPerson(e, "inspection_company")}
              >
                <i className="fa fa-pencil" /> Edit
              </a>
            )}
          </label>
          <InputSearch
            disabled={!userCanEdit}
            label="inspection_company"
            placeholder="Search for inspection company"
            icon="/assets/icons/Inspection Company.svg"
            results={allInspectors}
            value={inspectionCompany}
            toggleModal={toggleModal}
            handleChange={(label, value) => setInspectionCompany(value)}
            handleSearch={handleSearch}
          />

          <label>
            Broker or Agent{" "}
            {!!agent && (
              <a href="/" onClick={(e) => handleEditPerson(e, "agent")}>
                <i className="fa fa-pencil" /> Edit
              </a>
            )}
          </label>
          <InputSearch
            disabled={!userCanEdit}
            label="agent"
            placeholder="Search for broker or agent"
            icon="/assets/icons/Broker.svg"
            results={allAgents}
            value={agent}
            toggleModal={toggleModal}
            handleChange={(label, value) => setAgent(value)}
            handleSearch={handleSearch}
          />

          <label>Weather Conditions</label>
          <div className="md-diy-input-wrapper">
            <img
              src="/assets/icons/Weather.svg"
              className="md-diy-input-icon"
              alt=""
            />
            <input
              type="text"
              placeholder="Sunny"
              value={
                weather !== null
                  ? weather
                  : report.inspection_weather_comment || ""
              }
              onChange={(e) => setWeather(e.target.value)}
              disabled={!userCanEdit}
            />
          </div>

          {role === "PUBLISHER" && <label>Analyst</label>}
          {role === "PUBLISHER" && (
            <div>
              {majordomoPeopleRefId &&
                mdPeople.map((person) => {
                  if (person.majordomo_people_ref_id === majordomoPeopleRefId) {
                    return (
                      <div
                        className="md-person-card"
                        key={majordomoPeopleRefId}
                      >
                        {person.mdp_profile_pic || person.user_profile_pic ? (
                          <img
                            src={
                              person.mdp_profile_pic || person.user_profile_pic
                            }
                            alt="Profile"
                          />
                        ) : (
                          <div className="md-person-img-placeholder" />
                        )}
                        <div>
                          <p className="md-person-name">
                            <b>
                              {person.user_first_name.toUpperCase()}{" "}
                              {person.user_last_name.toUpperCase()}
                            </b>
                          </p>
                          <p>{person.mdp_title}</p>
                        </div>
                      </div>
                    );
                  } else {
                    return null;
                  }
                })}
              {!majordomoPeopleRefId && (
                <div className="md-person-card">
                  <div className="md-person-img-placeholder" />
                  <p>
                    <b>UNCLAIMED</b>
                  </p>
                </div>
              )}
            </div>
          )}

          <button
            href="#"
            className={`btn btn-primary ${updatingReport ? "disabled" : ""} ${
              !userCanEdit ? "d-none" : ""
            }`}
            disabled={updatingReport || !userCanEdit}
            onClick={updateReportHandler}
          >
            Save
          </button>
          {renderActionButtons()}
        </div>
      </div>

      {showPublishConfirmModal && (
        <ConfirmModal
          show={showPublishConfirmModal}
          close={handlePublishCancel}
          handleSubmit={handlePublishConfirm}
          title="Publish Report?"
          subtext="Are you sure you want to publish this report?"
          variant="primary"
          action="Publish"
        />
      )}
    </div>
  );
};

export default withRouter(InspectionDetails);
